import {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {buildCsvWithBom, toCsvRow} from '../../../../../_metronic/helpers'
import {getAllTasksByUserId} from '../../core/_requests'
import Swal from 'sweetalert2'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
})

const TasksByUserExportButton = () => {
  const intl = useIntl()
  const {userId} = useParams()
  const [isExporting, setIsExporting] = useState(false)

  const formatHoursFromSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    
    // Se já está no formato dd/mm/yyyy, retorna direto
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString
    }
    
    // Tenta parsear a data em formato ISO
    const date = new Date(dateString)
    
    // Verifica se a data é válida
    if (isNaN(date.getTime())) return ''
    
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const handleExportTasks = async () => {
    if (!userId) return

    setIsExporting(true)
    try {
      // Buscar todas as tarefas do usuário sem paginação
      const response = await getAllTasksByUserId(parseInt(userId))
      const tasks = response.data || []

      if (tasks.length === 0) {
        Toast.fire({
          icon: 'warning',
          title: 'Nenhuma tarefa encontrada para exportar',
          customClass: {
            popup: 'bg-light-warning',
            title: 'text-warning',
          },
        })
        return
      }

      const headers = [
        'ID',
        'Nome',
        'Status',
        'Prioridade',
        'Cliente',
        'Projeto',
        'Responsáveis',
        'Data de Início',
        'Data Esperada',
        'Data de Conclusão',
        'Data de Criação',
        'Horas Lançadas',
      ]
      const rows = tasks.map((task: any) =>
        toCsvRow([
          task.id,
          task.name || '',
          task.status || '',
          task.priority || '',
          task.client_name || '',
          task.project_name || '',
          task.users?.map((user: any) => user.name).join(', ') || '',
          formatDate(task.begin_date),
          formatDate(task.expected_date),
          formatDate(task.end_date),
          formatDate(task.created_at),
          formatHoursFromSeconds(task.total_time || 0),
        ])
      )
      const csvContent = buildCsvWithBom([toCsvRow(headers), ...rows])

      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `tarefas-usuario-${userId}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      Toast.fire({
        icon: 'success',
        title: `${tasks.length} tarefa(s) exportada(s) com sucesso!`,
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
    } catch (error) {
      console.error('Erro ao exportar tarefas:', error)
      Toast.fire({
        icon: 'error',
        title: 'Erro ao exportar tarefas',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    } finally {
      setIsExporting(false)
    }
  }
  
  return (
    <OverlayTrigger
      placement='top'
      delay={{show: 50, hide: 100}}
      overlay={<Tooltip>Exportar CSV</Tooltip>}
    >
      <button
        type='button'
        className='btn btn-sm btn-icon btn-light btn-hover-primary'
        onClick={handleExportTasks}
        disabled={isExporting}
      >
        {isExporting ? (
          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
        ) : (
          <i className='bi bi-download fs-4'></i>
        )}
      </button>
    </OverlayTrigger>
  )
}

export {TasksByUserExportButton}

